import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useSyncExternalStore, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import type { GlobalStateStore } from "@/lib/global-state";
import { loginWithReturnTo } from "@/lib/route-paths";

// The app's global-state surfaces, mounted once at the root above the Outlet.
//
// An **overlay** renders above mounted route state without unmounting it — that
// is the whole contract, and every choice here follows from it: the route below
// keeps its React state, so a half-finished edit is still there when the
// visitor comes back, and nothing on an overlay may reload the document.
//
// A **takeover** replaces the app's rendered content instead. Maintenance is
// the one takeover, and it is deliberately absent here: it needs the flag
// facade to decide it server-side, so it lands with #153. Its slot is already
// wired into the precedence order (lib/global-state.ts), so this component
// falls through to nothing rather than to an overlay ranked below it.

// A native modal <dialog>, not a z-indexed div. Both of the things an overlay
// has to promise are the browser's to keep here and nobody else's:
//
//   - It is above everything. `showModal()` puts the element in the top layer,
//     which outranks every z-index on the page — no bidding war with the sheet
//     at `z-[70]` or the focus-mode overlay at `z-[60]`, and no new ceiling for
//     the next component to overtake.
//   - Everything else is inert. The visitor cannot tab into work they can no
//     longer see, and the route below is only made inert — never unmounted —
//     so the edits it holds are still there afterwards.
//
// Neither overlay is dismissible: an expired session and a dead network are
// facts, not prompts, so Escape is refused.
function Overlay({ title, children }: { title: string; children?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || dialog.open) return;
    dialog.showModal();
    return () => dialog.close();
  }, []);

  return (
    <dialog
      ref={ref}
      role="alertdialog"
      aria-label={title}
      onCancel={(event) => event.preventDefault()}
      className="bg-lennar-grey-50/95 m-0 h-full max-h-none w-full max-w-none border-0 p-0 backdrop:bg-transparent"
    >
      <div className="flex h-full items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="border-lennar-grey-200 w-full max-w-md space-y-6 rounded-xl border bg-white p-8 text-center shadow-lg">
          <div className="flex justify-center">
            <img src="/lennar-logo.svg" alt="Lennar" className="h-6 w-auto" />
          </div>
          <h1 className="text-lennar-blue-600 text-2xl font-medium tracking-tight">{title}</h1>
          {children}
        </div>
      </div>
    </dialog>
  );
}

/**
 * Keeps the store's offline flag in step with the browser's own connectivity
 * events. A verbatim port of the old stack's `useOnlineStatus`: the events and
 * `navigator.onLine`, never a failed-fetch heuristic, which would mistake a
 * single 503 for a dead network.
 *
 * The initial read happens in the effect rather than at construction so the
 * hydration render still matches the server's, which never sees an overlay.
 */
function useOfflineTracking(store: GlobalStateStore) {
  useEffect(() => {
    // Each event asserts its own fact rather than re-reading navigator.onLine:
    // the flag and the events can disagree, and the event is the fresher of the
    // two. Only the initial read comes from the flag, since no event announces
    // a page that loaded offline.
    const goOnline = () => store.deactivate("offline");
    const goOffline = () => store.activate("offline");

    if (!navigator.onLine) goOffline();
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [store]);
}

export function GlobalStateOverlays({ store }: { store: GlobalStateStore }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const globalState = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getServerSnapshot,
  );

  useOfflineTracking(store);

  if (globalState === "offline") {
    return (
      <Overlay title="You appear to be offline">
        <p className="text-lennar-grey-600 text-base font-light">
          We can't reach the network right now. Your work is still here — this will clear itself as
          soon as you're back online.
        </p>
      </Overlay>
    );
  }

  if (globalState === "session-expired") {
    return (
      <Overlay title="Your session has expired">
        <p className="text-lennar-grey-600 text-base font-light">
          For your security, you've been signed out after a period of inactivity. Log in again to
          pick up where you left off.
        </p>
        {/* No Retry. A transient IdP outage no longer ends a session (#147), so
            by the time this overlay renders the session is genuinely gone and
            there is nothing a retry could recover. */}
        <Button
          className="w-full"
          onClick={() => {
            // Retired, not merely deactivated. The overlay belongs to the
            // expired session, not to the login page it is about to send the
            // visitor to — and a mutation still in flight will come back
            // unauthorized after this click. Raising a modal surface over the
            // sign-in page would lock them out of the one thing that fixes it.
            store.retire("session-expired");
            // And the cached identity goes with it. The _authed gate accepts a
            // current-user for its whole staleTime, so leaving it behind would
            // let a Back navigation re-enter the app on a session that is gone,
            // with no overlay this time. The backstop clears it for the same
            // reason (router-fallbacks.tsx); the two paths owe the same thing.
            queryClient.clear();
            void router.navigate(loginWithReturnTo(router.state.location.href));
          }}
        >
          Log in again
        </Button>
      </Overlay>
    );
  }

  return null;
}
